import { useState, useEffect } from 'react'
import { Shield, Mail, Lock, Loader } from 'lucide-react'
import { setAuthToken } from '../utils/auth'

interface PreviewAuthModalProps {
  tenantDomain: string
  version: 'preview' | 'last'
  onSuccess: () => void
}

/**
 * Enhanced authentication modal for preview/last site access
 * Primary: Google SSO
 * Fallback: Email verification code
 */
const PreviewAuthModal: React.FC<PreviewAuthModalProps> = ({ tenantDomain, version, onSuccess }) => {
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [step, setStep] = useState<'initial' | 'email' | 'code'>('initial')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  // Initialize Google Identity Services
  useEffect(() => {
    // Wait for GIS library to load
    if (typeof window.google === 'undefined') {
      console.warn('Google Identity Services SDK not loaded yet')
      return
    }

    try {
      const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID
      const isDevelopment = import.meta.env.DEV || window.location.hostname === 'localhost'
      const finalClientId = clientId || (isDevelopment ? '406298590928-2p9o3mjhfj6e8jb3rjd9lf44iivtfhqm.apps.googleusercontent.com' : '')

      if (!finalClientId) {
        console.error('❌ VITE_GOOGLE_CLIENT_ID not configured')
        return
      }

      if (!clientId && isDevelopment) {
        console.warn('⚠️ Using development Google Client ID')
      }

      window.google.accounts.id.initialize({
        client_id: finalClientId,
        callback: handleGoogleCredentialResponse,
        auto_select: false,
        cancel_on_tap_outside: false
      })

      // Render button
      const buttonElement = document.getElementById('preview-google-signin')
      if (buttonElement) {
        window.google.accounts.id.renderButton(buttonElement, {
          theme: 'outline',
          size: 'large',
          width: 350,
          text: 'continue_with',
          shape: 'rectangular'
        })
      }
    } catch (err) {
      console.error('Failed to initialize Google Sign-In:', err)
    }

    // Cleanup function to cancel GIS on unmount
    return () => {
      if (typeof window.google !== 'undefined' && window.google.accounts?.id) {
        window.google.accounts.id.cancel()
      }
    }
  }, [])

  // Handle Google credential response
  const handleGoogleCredentialResponse = async (response: any) => {
    try {
      setLoading(true)
      setError('')
      setSuccess('Verifying with Google...')

      const verifyResponse = await fetch('/api/auth/google/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential: response.credential })
      })

      // Check response status before parsing JSON
      if (!verifyResponse.ok) {
        let errorMessage = 'Google sign-in failed'
        try {
          const errorData = await verifyResponse.json()
          errorMessage = errorData.error || errorMessage
        } catch (parseErr) {
          // If JSON parsing fails, use HTTP status as context
          errorMessage = `Server error: ${verifyResponse.status}`
        }
        setError(errorMessage)
        setLoading(false)
        return
      }

      // Parse JSON only after confirming response is OK
      let data
      try {
        data = await verifyResponse.json()
      } catch (parseErr) {
        console.error('Failed to parse server response:', parseErr)
        setError('Invalid server response')
        setLoading(false)
        return
      }

      // Validate email exists before accessing
      if (!data?.email) {
        setError('Invalid response: missing email')
        setLoading(false)
        return
      }

      // Check domain matches
      const userDomain = data.email.split('@')[1]
      if (userDomain !== tenantDomain) {
        setError(`Please use an email from ${tenantDomain} to access this ${version} site`)
        setLoading(false)
        return
      }

      setSuccess('Login successful! Loading...')
      setAuthToken(data.token)
      document.cookie = `auth-token=${data.token}; path=/; max-age=86400; SameSite=Strict`

      setTimeout(() => onSuccess(), 500)
    } catch (err: any) {
      console.error('Google sign-in error:', err)
      setError(err.message || 'Failed to sign in with Google')
      setLoading(false)
    }
  }

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess('')

    // Validate email domain matches tenant
    const emailDomain = email.split('@')[1]
    if (emailDomain !== tenantDomain) {
      setError(`Please use an email from ${tenantDomain} to access this ${version} site`)
      return
    }

    setLoading(true)

    try {
      console.log('🔐 Requesting verification code for:', email)
      const response = await fetch('/api/auth/preview-access', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, action: 'send-code' })
      })

      const data = await response.json()
      console.log('📬 Preview auth response:', { status: response.status, data })

      if (response.ok) {
        setSuccess('Verification code sent to your email')
        setStep('code')
      } else {
        console.error('❌ Failed to send code:', data)
        setError(data.message || 'Failed to send code. Please try again.')
      }
    } catch (err) {
      console.error('❌ Network error:', err)
      setError('Network error. Please try again.')
    }

    setLoading(false)
  }

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess('')
    setLoading(true)

    try {
      const response = await fetch('/api/auth/preview-access', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code, action: 'verify-code' })
      })

      const data = await response.json()

      if (response.ok) {
        setSuccess('Verified! Loading...')
        // Store token
        setAuthToken(data.token)
        document.cookie = `auth-token=${data.token}; path=/; max-age=86400; SameSite=Strict`

        // Call success callback
        setTimeout(() => onSuccess(), 500)
      } else {
        setError(data.message || 'Invalid code. Please try again.')
      }
    } catch (err) {
      setError('Network error. Please try again.')
    }

    setLoading(false)
  }

  const handleResendCode = async () => {
    setError('')
    setSuccess('')
    setLoading(true)

    try {
      const response = await fetch('/api/auth/preview-access', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, action: 'send-code' })
      })

      if (response.ok) {
        setSuccess('New code sent to your email')
      } else {
        setError('Failed to resend code')
      }
    } catch (err) {
      setError('Network error. Please try again.')
    }

    setLoading(false)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-8">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
            <Shield className="w-8 h-8 text-blue-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Verify Access
          </h2>
          <p className="text-sm text-gray-600">
            This {version} site is only accessible to users from <span className="font-semibold">{tenantDomain}</span>
          </p>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
            {success}
          </div>
        )}

        {/* Initial Step - Google SSO + Email Option */}
        {step === 'initial' && (
          <div className="space-y-4">
            {/* Google Sign-In Button */}
            <div>
              <div id="preview-google-signin" className="flex justify-center"></div>
            </div>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Or use email verification</span>
              </div>
            </div>

            {/* Email Verification Option */}
            <button
              onClick={() => setStep('email')}
              className="w-full py-3 px-4 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700 font-medium flex items-center justify-center gap-2 transition"
            >
              <Mail className="w-5 h-5" />
              Continue with Email Code
            </button>
          </div>
        )}

        {/* Email Step */}
        {step === 'email' && (
          <form onSubmit={handleSendCode} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Work Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={`you@${tenantDomain}`}
                  required
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={loading}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Must be a {tenantDomain} email address
              </p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  Sending Code...
                </>
              ) : (
                <>
                  Send Verification Code
                </>
              )}
            </button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => {
                  setStep('initial')
                  setEmail('')
                  setError('')
                }}
                className="text-sm text-gray-600 hover:text-gray-700"
              >
                ← Back to sign-in options
              </button>
            </div>
          </form>
        )}

        {/* Code Step */}
        {step === 'code' && (
          <form onSubmit={handleVerifyCode} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Verification Code
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="Enter 6-digit code"
                  required
                  maxLength={6}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-2xl tracking-widest"
                  disabled={loading}
                  autoFocus
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Check your email ({email}) for the code
              </p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  Verifying...
                </>
              ) : (
                <>
                  Verify & Access
                </>
              )}
            </button>

            <div className="text-center">
              <button
                type="button"
                onClick={handleResendCode}
                disabled={loading}
                className="text-sm text-blue-600 hover:text-blue-700 disabled:opacity-50"
              >
                Didn't receive code? Resend
              </button>
            </div>

            <div className="text-center">
              <button
                type="button"
                onClick={() => {
                  setStep('initial')
                  setCode('')
                  setEmail('')
                  setError('')
                }}
                className="text-sm text-gray-600 hover:text-gray-700"
              >
                ← Back to sign-in options
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

export default PreviewAuthModal
